import {
  NoDataComponent,
  TableComponent,
  CustomButton,
  CustomSelect,
  CustomInput,
} from '@/shared/ui/components';
import { SearchIconV2 } from '@/shared/ui/assets';
import { convertSortOrder, renderSortIcon } from '@/shared/lib';
import { facilitySelection } from '@/entities/facility';
import _ from 'lodash';
import { t } from 'ttag';
import { useSupportRequest } from './useSupportRequest';
import {
  actionIconFormatter,
  categoryFormatter,
  cellFormatter,
  requestIdFormatter,
  requestStatusFormatter,
  requestorFormatter,
  rolesFormatter,
} from './handler';
import type { SupportRequestProps } from './supportRequest.types';
import './styles.scss';

/**
 * The support request tab: a filterable table of the facility's support tickets plus a button for creating a new request. Filters by status, category and request ID through useSupportRequest, builds its columns from the ticket formatters, and renders the table only when rows exist or are still loading and the user has support facilities, falling back to the no-data component otherwise.
 *
 * @param supportFacilities - Facilities the user may raise requests for.
 * @param selectedFacility - Currently selected facility scoping the ticket list.
 * @param isActive - Whether this tab is the selected one.
 * @returns The support request tab markup.
 */
const SupportRequest = ({ supportFacilities, selectedFacility, isActive }: SupportRequestProps) => {
  const {
    navigate,
    statusList,
    categoryList,
    state,
    filterObject,
    isShowFacilityName,
    onChangeSearchValue,
    handleClickViewRequestDetail,
    onChangeDropdown,
    handleTableChange,
  } = useSupportRequest({ supportFacilities, selectedFacility, isActive });

  /**
   * Builds the column definitions of the support request table. Emits request ID, status, category, requestor, requestor role and action columns, each rendering through its formatter with the current isLoadedData flag, applies the persisted sort order to the sortable request ID header, and includes the facility column only when isShowFacilityName is set.
   *
   * @returns Column descriptors for the support request table.
   */
  const generateColumns = () => [
    {
      dataIndex: 'ticketId',
      key: 'ticketId',
      title: t`Request ID`,
      sorter: true,
      width: '120px',
      fixed: 'left',
      sortOrder: convertSortOrder(undefined, filterObject.current),
      sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
      render: (cell: any, row: any, rowIndex: any) =>
        requestIdFormatter(cell, row, rowIndex, state.isLoadedData),
    },
    {
      dataIndex: 'status',
      key: 'status',
      width: 120,
      title: t`Status`,
      render: (cell: any, row: any, rowIndex: any) =>
        requestStatusFormatter(cell, row, rowIndex, state.isLoadedData),
    },
    ...(isShowFacilityName
      ? [
          {
            dataIndex: ['facility', 'name'],
            key: 'name',
            width: 265,
            title: t`Facility`,
            render: (cell: any, row: any, rowIndex: any) =>
              cellFormatter(cell, row, rowIndex, state.isLoadedData),
          },
        ]
      : []),
    {
      dataIndex: 'category',
      key: 'category',
      title: t`Category`,
      width: 180,
      render: (cell: any, row: any, rowIndex: any) =>
        categoryFormatter(cell, row, rowIndex, state.isLoadedData),
    },
    {
      dataIndex: ['requestedBy', 'firstName'],
      key: 'firstName',
      title: t`Requestor`,
      width: 180,
      render: (cell: any, row: any, rowIndex: any) =>
        requestorFormatter(cell, row, rowIndex, state.isLoadedData),
    },
    {
      dataIndex: ['requestedBy', 'roles'],
      key: 'roles',
      width: 150,
      title: t`Requestor role`,
      render: (cell: any, row: any, rowIndex: any) =>
        rolesFormatter(cell, row, rowIndex, state.isLoadedData),
    },
    {
      dataIndex: 'action',
      title: t`Actions`,
      fixed: 'right',
      width: '48px',
      onCell: () => ({ 'aria-label': t`Actions` }),
      render: (cell: any, row: any, rowIndex: any) =>
        actionIconFormatter(cell, row, rowIndex, state.isLoadedData, handleClickViewRequestDetail),
    },
  ];

  return (
    <div id="support-request-body" className="support-request-body">
      <div className="support-request-search-row">
        <CustomButton
          id="new-request-btn"
          onClick={() => {
            navigate('/support/new-support-request');
          }}
          size="small"
          tooltipPlacement="bottom"
          tooltipTitle={t`Create new request`}
        >
          {t`New Request`}
        </CustomButton>
        <div className="ml-auto flex">
          <CustomSelect
            className="status-input"
            name="status"
            options={_.map(statusList, (item) => ({ label: item, value: item }))}
            value={state.status}
            onChange={onChangeDropdown}
            size="middle"
            showSearch
            allowClear={false}
            placement="bottomRight"
            popupMatchSelectWidth={false}
            aria-label={t`Status`}
          />
          <CustomSelect
            className="request-input"
            name="categories"
            options={_.map(categoryList, (item) => ({ label: item, value: item }))}
            value={state.categories}
            onChange={onChangeDropdown}
            size="middle"
            showSearch
            allowClear={false}
            placement="bottomRight"
            popupMatchSelectWidth={false}
            aria-label={t`Category`}
          />
          <CustomInput
            className="search-input"
            name="searchValue"
            aria-label={`${t`Search by`} ${t`Request ID`}`}
            value={state.searchValue}
            onChange={onChangeSearchValue}
            placeholder={`${t`Search by`} ${t`Request ID`}`}
            size="middle"
            type="number"
            suffix={<img src={SearchIconV2} alt="Search Icon" />}
            allowClear
          />
        </div>
      </div>
      {(state.data.length || (!state.data.length && !state.isLoadedData)) &&
      (facilitySelection.userSupportFacilities()?.length > 0 || supportFacilities?.length > 0) ? (
        <TableComponent
          className="support-request-table"
          data={state.data || []}
          isLoading={!state.isLoadedData}
          columns={generateColumns()}
          handleTableChange={handleTableChange}
          currentPage={state.page}
          pageSize={state.sizePerPage}
          isEndOfData={state.isEndOfData}
          ticketCount={state.clinicTicketCount}
          // scrollX={1184}
        />
      ) : (
        <div className="no-data">
          <NoDataComponent isSearching={!!filterObject.current.ticketId} />
        </div>
      )}
    </div>
  );
};

export default SupportRequest;
